import { useMemo } from 'react';
import { getTranslateFunction } from '../api/util/getTranslateFunction';
import CUTranslationsAPI from '../api/CUTranslationAPI';
import { skipToken } from '@reduxjs/toolkit/query';
import { ControlUnitTranslationAPIResponse } from '../api/CUTranslation.model';
import { orComparator } from '../../../util/lodash/orComparator';
import _ from 'lodash';

/**
 * Custom hook to get translation function for Control Units using RTK Query
 *
 * This hook:
 * 1. Fetches translation info metadata for control units from the API
 * 2. Fetches translation info metadata for commander from the API
 * 3. Downloads translation files from S3 using signed URLs
 * 4. Creates a translate function with control unit and commander translations
 *
 * @param deviceId - The device ID
 * @param controlUnitId - Optional control unit ID to scope translations
 * @returns Tuple of [translateFunction, isLoading, error]
 */
export const useCUTranslate = (deviceId: string, controlUnitId?: string) => {
  // Fetch commander translation info and file
  const { data: commanderTranslationInfo, ...restCommanderTranslations } =
    CUTranslationsAPI.useGetCommanderTranslationInfoQuery(undefined);

  const { data: controlUnitInfo, ...restControlUnitInfo } =
    CUTranslationsAPI.useGetCUTranslationInfoQuery(
      {
        deviceId: deviceId,
        controlUnitId: controlUnitId!,
      },
      { skip: !controlUnitId || !deviceId }
    );

  const { data: commanderTranslationData, ...restCommanderFileTranslations } =
    CUTranslationsAPI.useDownloadTranslationFileQuery(
      commanderTranslationInfo ?? skipToken
    );

  const { data: controlUnitTranslationData, ...restControlUnitFile } =
    CUTranslationsAPI.useDownloadTranslationFileQuery(
      controlUnitInfo?.file ?? skipToken
    );

  const CUTranslate = useMemo(
    () =>
      getTranslateFunction(
        controlUnitTranslationData as ControlUnitTranslationAPIResponse,
        commanderTranslationData,
        controlUnitId
      ),
    [controlUnitTranslationData, commanderTranslationData, controlUnitId]
  );

  const response = useMemo(
    () =>
      _.chain({ ...restCommanderTranslations, CUTranslate, data: CUTranslate })
        .mergeWith(restControlUnitInfo, orComparator)
        .mergeWith(restCommanderFileTranslations, orComparator)
        .mergeWith(restControlUnitFile, orComparator)
        .value(),
    [
      restCommanderTranslations,
      CUTranslate,
      restControlUnitInfo,
      restCommanderFileTranslations,
      restControlUnitFile,
    ]
  );

  return response;
};
